// FE-SCRM-048 — Standalone CSV import wizard at
// /dashboard/sales-crm/import.
//
// Step routing is derived from URL state + the session detail. URL
// holds ``?type=prospects|sales_contacts&session=<uuid>``; with no
// type we render the chooser, with type-but-no-session we render the
// upload step, and with both we route by session.status. This keeps
// reloads and shared links replayable without per-step component
// state to keep in sync.

import { useMemo } from "react";
import {
  useNavigate, useSearchParams,
} from "react-router-dom";

import { ApiError } from "@/api/client";
import { Toast } from "@/design-system";

import { CommitPreviewStep } from "./CommitPreviewStep";
import { ImportChooserCards } from "./ImportChooserCards";
import {
  useCommitImportMutation,
  useCreateImportMutation,
  useImportDetailQuery,
} from "./hooks";
import { SuccessStep } from "./SuccessStep";
import { UploadStep } from "./UploadStep";
import { ValidationStep } from "./ValidationStep";
import type { ImportSession, ImportType } from "./types";


const VALID_TYPES: readonly ImportType[] = [
  "prospects", "sales_contacts",
];


function _readType(raw: string | null): ImportType | null {
  if (raw && (VALID_TYPES as readonly string[]).includes(raw)) {
    return raw as ImportType;
  }
  return null;
}


export function ImportPage() {
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();

  const import_type = useMemo(
    () => _readType(search.get("type")), [search],
  );
  const session_id = search.get("session");

  // The detail query is the source of truth once we have a session.
  // While the commit is in flight the server flips status from
  // ``validated`` → ``committing`` → ``completed`` / ``failed``; we
  // refetch every second only while the row is in ``committing`` so
  // the UI catches the transition without polling forever.
  const detail_query = useImportDetailQuery(session_id, {
    refetchInterval: false,
  });
  const session: ImportSession | undefined = detail_query.data ?? undefined;

  const create_mutation = useCreateImportMutation();
  const commit_mutation = useCommitImportMutation();

  function _setParams(next: { type?: ImportType | null; session?: string | null }) {
    const params = new URLSearchParams(search);
    if (next.type === null) params.delete("type");
    else if (next.type) params.set("type", next.type);
    if (next.session === null) params.delete("session");
    else if (next.session) params.set("session", next.session);
    setSearch(params, { replace: true });
  }

  function handleFile(file: File) {
    if (!import_type) return;
    create_mutation.mutate(
      { import_type, file },
      {
        onSuccess: (created) => {
          _setParams({ session: created.session_id });
        },
      },
    );
  }

  function handleReupload() {
    _setParams({ session: null });
    create_mutation.reset();
  }

  function handleConfirmCommit() {
    if (!session) return;
    commit_mutation.mutate(session.session_id);
  }

  function handleRestart() {
    _setParams({ session: null, type: null });
    create_mutation.reset();
    commit_mutation.reset();
  }

  const upload_error = _humanizeMutationError(create_mutation.error);

  return (
    <div
      data-testid="sales-import-page"
      className="flex flex-col gap-5"
    >
      <header className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-lg font-semibold tracking-tight text-text-primary">
            Import data
          </h1>
          <p className="text-xs text-text-secondary">
            CSV bulk import for prospects and sales contacts. Two
            phases: we validate first, then commit only after you
            confirm.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/dashboard/sales-crm?tab=settings")}
          className="text-xs font-medium text-teal hover:underline"
        >
          Back to Settings
        </button>
      </header>

      <Stepper
        import_type={import_type}
        session={session}
      />

      {/* Step routing. */}
      {!import_type && (
        <ImportChooserCards />
      )}

      {import_type && !session_id && (
        <UploadStep
          import_type={import_type}
          on_file={handleFile}
          is_uploading={create_mutation.isPending}
          upload_error={upload_error}
        />
      )}

      {import_type && session_id && detail_query.isLoading && (
        <p
          data-testid="import-detail-loading"
          className="text-sm text-text-secondary"
        >
          Loading import session…
        </p>
      )}

      {import_type && session && session.status === "validated"
        && (session.validation_report?.error_count ?? 0) > 0 && (
        <ValidationStep
          session={session}
          on_continue={() => undefined}
          on_reupload={handleReupload}
        />
      )}

      {import_type && session && session.status === "validated"
        && (session.validation_report?.error_count ?? 0) === 0 && (
        <>
          <ValidationStep
            session={session}
            on_continue={handleConfirmCommit}
            on_reupload={handleReupload}
          />
          <CommitPreviewStep
            session={session}
            on_confirm={handleConfirmCommit}
            on_cancel={handleReupload}
            is_committing={commit_mutation.isPending}
          />
        </>
      )}

      {import_type && session && session.status === "committing" && (
        <CommittingState />
      )}

      {import_type && session && session.status === "completed" && (
        <>
          <SuccessStep session={session} on_restart={handleRestart} />
          <Toast
            variant="success"
            title={_successToastTitle(session)}
          />
        </>
      )}

      {import_type && session && session.status === "failed" && (
        <FailureState
          session={session}
          on_retry={handleConfirmCommit}
          on_reupload={handleReupload}
          is_retrying={commit_mutation.isPending}
        />
      )}

      {import_type && session && session.status === "expired" && (
        <ExpiredState on_restart={handleRestart} />
      )}
    </div>
  );
}


function Stepper({
  import_type, session,
}: { import_type: ImportType | null; session: ImportSession | undefined }) {
  const step = _computeStepIndex(import_type, session);
  if (step < 0) return null;
  const labels = ["Upload", "Validate", "Confirm", "Done"];
  return (
    <ol
      data-testid="import-stepper"
      className="flex items-center gap-2 text-xs text-text-tertiary"
    >
      {labels.map((label, idx) => (
        <li
          key={label}
          className={[
            "flex items-center gap-2",
            idx === step ? "font-medium text-text-primary" : "",
          ].join(" ")}
        >
          <span
            className={[
              "flex h-5 w-5 items-center justify-center rounded-full",
              "border border-border-subtle tabular-nums",
              idx === step ? "bg-teal text-text-inverse border-teal" : "",
              idx < step ? "bg-recessed text-text-secondary" : "",
            ].join(" ")}
          >
            {idx + 1}
          </span>
          <span>{label}</span>
          {idx < labels.length - 1 && (
            <span aria-hidden className="text-text-tertiary">
              →
            </span>
          )}
        </li>
      ))}
    </ol>
  );
}


function CommittingState() {
  return (
    <div
      data-testid="import-step-committing"
      className="flex flex-col items-center gap-3 rounded-lg bg-recessed py-10 text-center"
    >
      <div
        className="h-1 w-48 overflow-hidden rounded-full bg-border-subtle"
        role="progressbar"
        aria-label="Importing"
      >
        <div
          className="h-full w-1/2 animate-pulse bg-teal"
        />
      </div>
      <p className="text-sm text-text-secondary">
        Creating rows. This usually takes a few seconds.
      </p>
    </div>
  );
}


function FailureState({
  session, on_retry, on_reupload, is_retrying,
}: {
  session: ImportSession;
  on_retry: () => void;
  on_reupload: () => void;
  is_retrying: boolean;
}) {
  const summary_error = session.commit_summary?.error;
  return (
    <div
      data-testid="import-step-failure"
      className="flex flex-col gap-3 rounded-md border border-ember bg-raised p-4"
    >
      <h2 className="text-base font-semibold text-ember">
        Import didn't finish
      </h2>
      <p className="text-xs text-text-secondary">
        {summary_error
          ?? "Something went wrong on our side while writing your rows."}
        {" "}You can retry without re-uploading, or start over with a
        fresh file.
      </p>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={on_reupload}
          className="text-xs font-medium text-text-secondary hover:underline"
        >
          Start over
        </button>
        <button
          type="button"
          onClick={on_retry}
          disabled={is_retrying}
          className={[
            "rounded-md bg-teal px-3 py-1.5 text-xs font-medium",
            "text-text-inverse hover:opacity-90 disabled:opacity-50",
          ].join(" ")}
        >
          {is_retrying ? "Retrying…" : "Retry"}
        </button>
      </div>
    </div>
  );
}


function ExpiredState({ on_restart }: { on_restart: () => void }) {
  return (
    <div
      data-testid="import-step-expired"
      className="flex flex-col gap-3 rounded-md border border-border-subtle bg-raised p-4"
    >
      <h2 className="text-base font-semibold text-text-primary">
        This import session has expired
      </h2>
      <p className="text-xs text-text-secondary">
        Import sessions expire 24 hours after upload. Upload your CSV
        again to start a fresh session.
      </p>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={on_restart}
          className={[
            "rounded-md bg-teal px-3 py-1.5 text-xs font-medium",
            "text-text-inverse hover:opacity-90",
          ].join(" ")}
        >
          Start a new import
        </button>
      </div>
    </div>
  );
}


function _computeStepIndex(
  import_type: ImportType | null, session: ImportSession | undefined,
): number {
  if (!import_type) return -1;
  if (!session) return 0;
  if (session.status === "validated") {
    return (session.validation_report?.error_count ?? 0) > 0 ? 1 : 2;
  }
  if (session.status === "committing") return 2;
  if (session.status === "completed") return 3;
  if (session.status === "failed") return 2;
  return 0;
}


function _humanizeMutationError(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    const body = err.body ?? {};
    if (Array.isArray(body.missing_columns)) {
      return (
        "Your CSV is missing required columns: "
        + (body.missing_columns as string[]).join(", ")
      );
    }
    if (typeof body.detail === "string") return body.detail;
    return `Upload failed (HTTP ${err.status}).`;
  }
  if (err instanceof Error) return err.message;
  return "Upload failed.";
}


function _successToastTitle(session: ImportSession): string {
  const summary = session.commit_summary ?? {};
  if (session.import_type === "prospects") {
    const n = summary.prospects_created ?? 0;
    return `${n} prospect${n === 1 ? "" : "s"} imported`;
  }
  const n = summary.contacts_created ?? 0;
  return `${n} sales contact${n === 1 ? "" : "s"} imported`;
}
